/*
 * Modules dependencies
 */

import { BadgePrice } from 'src/components/core/badges/badge-price';
import { Button } from 'src/components/core/buttons/button';
import { Image } from 'src/components/core/image';
import { Text } from 'src/components/core/text';
import { breakpoints } from '@untile/react-core/styles/breakpoints';
import { media } from '@untile/react-core/styles/media';
import { transparentize } from 'src/styles/utils/colors';
import { useTranslation } from 'next-i18next';
import linkIcon from 'src/assets/svgs/link.svg';
import styled from 'styled-components';

/*
 * Export `CardServiceJobsProps` types
 */

export type CardServiceJobsProps = {
  alternative?: boolean;
  badgePrice?: string;
  button?: {
    href: string;
    label: string;
  };
  description: string;
  image: { alt: string; src: string };
  title: string;
};

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  background: var(--gradient-secondary--45-deg);
  border-radius: var(--border-radius-sm);
  display: grid;
  grid-template-areas: 'image' 'content';
  grid-template-rows: 168px auto;
  height: 100%;

  ${media.min.md`
    grid-template-areas: 'content image';
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 320px;
  `}

  &[data-style-alternative='true'] {
    grid-template-areas: 'content' 'image';
    grid-template-rows: auto 323px;

    ${media.min.md`
      grid-template-areas: 'content image';
      grid-template-rows: 420px;
    `}
  }
`;

/*
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  align-items: flex-end;
  border-radius: var(--border-radius-sm);
  display: flex;
  grid-area: image;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

/**
 * `StyledBadgePrice` styled component.
 */

const StyledBadgePrice = styled(BadgePrice)`
  bottom: 32px;
  position: absolute;
  right: 32px;
`;

/*
 * `Content` styled component.
 */

const Content = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: content;
  max-width: 524px;
  padding: var(--space-ms) calc(var(--space-sm) * 3) var(--space-sm) var(--space-sm);
`;

/**
 * Export `CardServiceJobs` component.
 */

export const CardServiceJobs = ({
  alternative,
  badgePrice,
  button,
  description,
  image,
  title
}: CardServiceJobsProps) => {
  const { t } = useTranslation();

  return (
    <Grid data-style-alternative={alternative}>
      <ImageWrapper>
        <div
          style={{
            alignItems: 'flex-end',
            backgroundColor: alternative ? `${transparentize('cyan800', 0.6)}` : 'transparent',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            position: 'relative',
            width: '100%'
          }}
        >
          {alternative ? (
            <Image
              alt={image?.alt || title}
              height={718}
              sizes={`(max-width: ${breakpoints.md}px) 100vw, (max-width: ${breakpoints.lg}px) 50vw, 50vw`}
              src={image.src}
              style={{
                height: '100%',
                position: 'relative',
                width: 'auto'
              }}
              width={718}
            />
          ) : (
            <Image
              alt={image?.alt || title}
              fill
              sizes={`(max-width: ${breakpoints.md}px) 100vw, (max-width: ${breakpoints.lg}px) 50vw, 50vw`}
              src={image.src}
              style={{ objectFit: 'cover' }}
            />
          )}

          {badgePrice && (
            <StyledBadgePrice>
              <Text as={'p'} style={{ gridArea: 'price', lineHeight: '100%' }} variant={'h3'} weight={600}>
                {badgePrice}
              </Text>

              <Text as={'p'} style={{ gridArea: 'price-symbol', lineHeight: '100%' }} variant={'h4'} weight={600}>
                {'€'}
              </Text>

              <Text as={'p'} style={{ gridArea: 'vat', textAlign: 'center' }} variant={'bodyXxs'}>
                {t('common:labels.vat')}
              </Text>
            </StyledBadgePrice>
          )}
        </div>
      </ImageWrapper>

      <Content>
        <Text
          as={'p'}
          style={{ color: 'var(--color-cyan50)', marginBottom: 'var(--space-xs)' }}
          variant={'h3'}
          weight={600}
        >
          {title}
        </Text>

        <Text
          as={'p'}
          style={{ color: 'var(--color-cyan50)', marginBottom: button ? 'var(--space-ms)' : 0 }}
          variant={'bodyL'}
          weight={400}
        >
          {description}
        </Text>

        {button && (
          <Button
            aria-label={button.label}
            as={'a'}
            colorTheme={'ghost'}
            href={button.href}
            icon={linkIcon}
            rel={'noopener noreferrer'}
            reverse
            size={'medium'}
            style={{ marginTop: 'auto', width: 'fit-content' }}
            target={'_blank'}
          >
            {button.label}
          </Button>
        )}
      </Content>
    </Grid>
  );
};
